import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import {
  IAllFeatureIdsQuery as IAllSanityFeatureIdsQuery,
  useAllFeatureIdsQuery as useAllSanityFeatureIdsQuery,
  useFeatureNutritionLazyQuery,
} from 'generated/sanity-graphql';
import { usePrevious } from 'hooks/use-previous';

interface IFeatureIds {
  // Sanity feature Ids
  featureDonationId?: string;
  featureFoodwareId?: string;
  featureHomePageId?: string;
  featureMenuId?: string;
  // SanityBrand feature IDs
  featureAccountId?: string;
  featureAccountDeletePageId?: string;
  featureAccountFormId?: string;
  featureAccountRequestInfoPageId?: string;
  featureBeeperId?: string;
  featureClaimPointsUIId?: string;
  featureDisclaimersId?: string;
  featureGeolocationMapMarkersId?: string;
  featureGeolocationModalId?: string;
  featureGiftId?: string;
  featureLayoutId?: string;
  featureLinkPhysicalCardUIId?: string;
  featureLoyaltyRewardListCategoriesId?: string;
  featureLoyaltyUIId?: string;
  featureNavigationId?: string;
  featureNutritionId?: string;
  featureOffersId?: string;
  featureStaticMenuId?: string;
  featureSupportDataId?: string;
}

export interface IFeaturesContext extends IFeatureIds {
  featureIdsLoading: boolean;
  refetchFeatures: () => void;
}

export const FeaturesContext = createContext<IFeaturesContext>({
  featureIdsLoading: true,
  refetchFeatures: () => null,
});

export const useFeaturesContext = () => useContext(FeaturesContext);

export const HOMEPAGE_ID = 'feature-home-page-singleton';

const processAllSanityFeatureIds = (data: IAllSanityFeatureIdsQuery | undefined): IFeatureIds => {
  if (!data) {
    return {};
  }

  const getSingletonId = (id: keyof Omit<IAllSanityFeatureIdsQuery, '__typename'>) => {
    return data?.[id]?.[0]?._id;
  };

  return {
    featureDonationId: getSingletonId('allFeatureDonations'),
    featureFoodwareId: getSingletonId('allFeatureFoodwares'),
    featureHomePageId: HOMEPAGE_ID,
    featureMenuId: getSingletonId('allFeatureMenus'),
    featureAccountDeletePageId: getSingletonId('allFeatureAccountDeletePages'),
    featureAccountFormId: getSingletonId('allFeatureAccountForms'),
    featureAccountId: getSingletonId('allFeatureAccounts'),
    featureAccountRequestInfoPageId: getSingletonId('allFeatureAccountRequestInfoPages'),
    featureBeeperId: getSingletonId('allFeatureBeeperModals'),
    featureClaimPointsUIId: getSingletonId('allClaimPointsUis'),
    featureDisclaimersId: getSingletonId('allFeatureDisclaimers'),
    featureGeolocationMapMarkersId: getSingletonId('allFeatureGeolocationMapMarkers'),
    featureGeolocationModalId: getSingletonId('allFeatureGeolocationModals'),
    featureGiftId: getSingletonId('allFeatureGifts'),
    featureLayoutId: getSingletonId('allFeatureLayouts'),
    featureLinkPhysicalCardUIId: getSingletonId('allLinkPhysicalCardUis'),
    featureLoyaltyRewardListCategoriesId: getSingletonId('allRewardLists'),
    featureLoyaltyUIId: getSingletonId('allLoyaltyUis'),
    featureNavigationId: getSingletonId('allFeatureNavigations'),
    featureNutritionId: getSingletonId('allFeatureNutritions'),
    featureOffersId: getSingletonId('allFeatureOffers'),
    featureStaticMenuId: getSingletonId('allFeatureStaticMenus'),
    featureSupportDataId: getSingletonId('allSupportData'),
  };
};

export const FeaturesProvider: React.FC<React.PropsWithChildren<IBaseProps>> = ({ children }) => {
  const {
    data: allSanityFeatureIds,
    loading: sanityFeatureIdsLoading,
    refetch: refetchSanityFeatureIds,
  } = useAllSanityFeatureIdsQuery();
  const sanityFeatureIds = useMemo(() => processAllSanityFeatureIds(allSanityFeatureIds), [
    allSanityFeatureIds,
  ]);

  const [dispatchFeatureNutritionQuery] = useFeatureNutritionLazyQuery({
    fetchPolicy: 'network-only',
  });
  const { featureNutritionId } = sanityFeatureIds;
  const previousFeatureNutritionId = usePrevious(featureNutritionId);

  useEffect(() => {
    // pre-fetch critical data after feature nutrition id changes
    if (previousFeatureNutritionId !== featureNutritionId && featureNutritionId) {
      const prefetch = async () => {
        dispatchFeatureNutritionQuery({
          variables: { featureNutritionId },
        });
      };
      prefetch();
    }
  }, [dispatchFeatureNutritionQuery, featureNutritionId, previousFeatureNutritionId]);

  const refetchFeatures = useCallback(() => {
    refetchSanityFeatureIds();
  }, [refetchSanityFeatureIds]);

  const contextValue = useMemo(() => {
    return {
      ...sanityFeatureIds,
      featureIdsLoading: sanityFeatureIdsLoading,
      refetchFeatures,
    };
  }, [sanityFeatureIds, sanityFeatureIdsLoading, refetchFeatures]);

  return <FeaturesContext.Provider value={contextValue}>{children}</FeaturesContext.Provider>;
};
